import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Spinner } from '../components/Components';
import { coachAPI } from '../utils/api';
import toast from 'react-hot-toast';
import './Availability.css';

const TIME_SLOTS = [
  '06:00 AM','06:30 AM','07:00 AM','07:30 AM','08:00 AM','08:30 AM',
  '09:00 AM','09:30 AM','10:00 AM','10:30 AM','11:00 AM','11:30 AM',
  '12:00 PM','12:30 PM','01:00 PM','01:30 PM','02:00 PM','02:30 PM',
  '03:00 PM','03:30 PM','04:00 PM','04:30 PM','05:00 PM','05:30 PM',
  '06:00 PM','06:30 PM','07:00 PM','07:30 PM','08:00 PM','08:30 PM',
  '09:00 PM',
];

export default function ManageAvailability() {
  const { user, updateUser } = useAuth();
  const [availability, setAvailability] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Generate next 14 days
  const days = [];
  const today = new Date();
  for (let i = 0; i < 14; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    days.push(d);
  }

  useEffect(() => {
    // Load existing availability
    if (user?.availability) {
      setAvailability({ ...user.availability });
    }
    setLoading(false);
  }, [user]);

  const toggleSlot = (dateStr, time) => {
    setAvailability(prev => {
      const current = prev[dateStr] || [];
      const updated = current.includes(time)
        ? current.filter(t => t !== time)
        : [...current, time].sort();
      return { ...prev, [dateStr]: updated };
    });
  };

  const selectAllForDay = (dateStr) => {
    setAvailability(prev => {
      const current = prev[dateStr] || [];
      if (current.length === TIME_SLOTS.length) {
        return { ...prev, [dateStr]: [] };
      }
      return { ...prev, [dateStr]: [...TIME_SLOTS] };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await coachAPI.updateAvailability({ availability });
      updateUser({ ...user, availability: res.data.availability });
      toast.success('Availability updated for next 2 weeks! ✅');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    } finally { setSaving(false); }
  };

  const getRelativeDay = (d) => {
    const t = new Date(); t.setHours(0,0,0,0);
    const target = new Date(d); target.setHours(0,0,0,0);
    const diff = Math.round((target - t) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff === 2) return 'Day After Tomorrow';
    return d.toLocaleDateString('en-US', { weekday: 'long' });
  };

  if (loading) return <div className="page-container"><Spinner /></div>;

  return (
    <div className="page-container" style={{ maxWidth: 1000 }}>
      <div className="avail-header">
        <div>
          <h2 className="avail-title">Manage Availability</h2>
          <p className="avail-subtitle">Set your available slots for the next 2 weeks. Click time slots to toggle.</p>
        </div>
        <Button variant="success" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : '💾 Save All Changes'}
        </Button>
      </div>

      <div className="avail-days">
        {days.map(day => {
          const dateStr = day.toISOString().split('T')[0];
          const slots = availability[dateStr] || [];
          const relDay = getRelativeDay(day);
          const dateLabel = day.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

          return (
            <Card key={dateStr} className="avail-day-card">
              <div className="avail-day-header">
                <div className="avail-day-header__left">
                  <span className={`avail-day-tag ${relDay === 'Today' ? 'avail-day-tag--today' : relDay === 'Tomorrow' ? 'avail-day-tag--tomorrow' : ''}`}>
                    {relDay}
                  </span>
                  <span className="avail-day-date">{dateLabel}</span>
                </div>
                <div className="avail-day-header__right">
                  <span className="avail-day-count">{slots.length} slot{slots.length !== 1 ? 's' : ''}</span>
                  <button className="avail-select-all" onClick={() => selectAllForDay(dateStr)}>
                    {slots.length === TIME_SLOTS.length ? 'Clear All' : 'Select All'}
                  </button>
                </div>
              </div>
              <div className="avail-slots-grid">
                {TIME_SLOTS.map(time => {
                  const isSelected = slots.includes(time);
                  return (
                    <button
                      key={time}
                      className={`avail-slot ${isSelected ? 'avail-slot--selected' : ''}`}
                      onClick={() => toggleSlot(dateStr, time)}
                    >
                      {time}
                    </button>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>

      <div className="avail-footer">
        <Button variant="success" size="lg" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : '💾 Save Availability'}
        </Button>
      </div>
    </div>
  );
}
